/**
 * The language pick (i18n.md §2, R855, R2207).
 *
 * `gen/strings.load.ts` calls `pickLanguage` ONCE, when it evaluates, before
 * any other module has read a string. A module that copies a sentence
 * into a constant at load time therefore sees the picked language, and
 * the render loop pays nothing for the second language
 * (ui-map-interface.md §10). A change of the LANGUAGE setting reloads
 * the page, and the pick runs again.
 *
 * The effective language is:
 *
 *   1. the `lang` field of the stored settings blob, where it names a
 *      supported language;
 *   2. otherwise the source language, the first supported tag.
 *
 * The pick never reads the language of the browser (R2207): English is
 * the language until the player picks another one in the LANGUAGE row.
 * A blob of an older build that says `auto` reads as the source language.
 *
 * `pickFrom` is pure, so the rule is testable with no browser. The
 * wrapper reads the local store through `readStore`: a private window, a
 * blocked store and a test runner with no DOM must all answer a language
 * rather than throw.
 */

import { isObject } from '../net/json';
import { readStore } from '../ui/localstore';
import { SETTINGS_KEY } from '../ui/settingskey';

/**
 * Picks the language from values a caller states. `stored` is the `lang`
 * field of the settings blob (a tag, an old `auto`, or nothing).
 */
export function pickFrom(supported: readonly string[], stored?: string | null): string {
  if (stored && supported.includes(stored)) return stored;
  return supported[0] ?? 'en';
}

/** The stored `lang` field, or null where nothing is stored. */
function storedLanguage(): string | null {
  const raw = readStore(SETTINGS_KEY);
  if (!raw) return null;
  try {
    const obj: unknown = JSON.parse(raw);
    return isObject(obj) && typeof obj.lang === 'string' ? obj.lang : null;
  } catch {
    return null;
  }
}

/** The effective language of this page. */
export function pickLanguage(supported: readonly string[], stored?: string | null): string {
  return pickFrom(supported, stored === undefined ? storedLanguage() : stored);
}

/**
 * Writes the picked language into the document, so the browser hyphenates
 * and a screen reader speaks the right language. Each entry calls it at
 * boot.
 */
export function applyDocumentLanguage(tag: string): void {
  try {
    document.documentElement.lang = tag;
  } catch { /* no DOM: a test runner, a worker */ }
}
